import React, { useState, useEffect } from 'react';
import { Alert, Box, Button, CircularProgress, InputBase, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { WizardData } from '../../../context/WizardContext';
import { tokens } from '../../../theme/tokens';
import {
  ColHeader,
  CompactRow,
  CompactSection,
  householdBlockSx,
  nameEditorSx,
  personGridSx,
} from './clientWorkspace/clientDetails/compactPrimitives';
import { bannerQuietButtonSx, bannerRunButtonSx } from './clientWorkspace/clientDetails/StepBanner';
import { ClipboardGlyph, ClockGlyph, UserGlyph } from './clientWorkspace/clientDetails/detailsIcons';

interface PlanRequestFormProps {
  initialData?: Partial<WizardData>;
  customerId: string;
  onSubmit: (data: WizardData, specialInstructions: string) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  readOnlyClientFields?: boolean; // Make client identity fields read-only (for revisions)
}

interface FormErrors {
  [key: string]: string;
}

/**
 * Single-page plan request form, laid out like the Client Details tab —
 * Bio / Income / Accounts sections with a column per person — so creating a
 * household reads exactly like editing one. Real-time validation; PlanScout
 * estimates anything left blank.
 */
// customerId stays on the props contract for the modal; the advisor is chosen
// there, and this form only shapes the WizardData.
const PlanRequestForm: React.FC<PlanRequestFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitting = false,
  readOnlyClientFields = false
}) => {
  // Form state
  const [hasPartner, setHasPartner] = useState(initialData?.personal?.hasPartner ?? false);
  const [client1FirstName, setClient1FirstName] = useState(initialData?.personal?.client1?.firstName ?? '');
  const [client1LastName, setClient1LastName] = useState(initialData?.personal?.client1?.lastName ?? '');
  const [client1DOB, setClient1DOB] = useState(initialData?.personal?.client1?.dateOfBirth ?? '');
  const [client2FirstName, setClient2FirstName] = useState(initialData?.personal?.client2?.firstName ?? '');
  const [client2LastName, setClient2LastName] = useState(initialData?.personal?.client2?.lastName ?? '');
  const [client2DOB, setClient2DOB] = useState(initialData?.personal?.client2?.dateOfBirth ?? '');
  const [contactEmail, setContactEmail] = useState(initialData?.personal?.contact?.email ?? '');

  const [client1Income, setClient1Income] = useState(initialData?.employment?.client1?.currentIncome ?? 0);
  const [client1RetirementAge, setClient1RetirementAge] = useState(initialData?.employment?.client1?.expectedRetirementAge ?? 0);
  const [client1PensionStartAge, setClient1PensionStartAge] = useState(initialData?.employment?.client1?.pensionStartAge ?? 0);
  const [client1PensionAmount, setClient1PensionAmount] = useState(initialData?.employment?.client1?.pensionBenefitAmount ?? 0);

  const [client2Income, setClient2Income] = useState(initialData?.employment?.client2?.currentIncome ?? 0);
  const [client2RetirementAge, setClient2RetirementAge] = useState(initialData?.employment?.client2?.expectedRetirementAge ?? 0);
  const [client2PensionStartAge, setClient2PensionStartAge] = useState(initialData?.employment?.client2?.pensionStartAge ?? 0);
  const [client2PensionAmount, setClient2PensionAmount] = useState(initialData?.employment?.client2?.pensionBenefitAmount ?? 0);

  const [client1CurrentSS, setClient1CurrentSS] = useState(initialData?.socialSecurity?.client1?.currentAmount ?? 0);
  const [client1ProjectedSS, setClient1ProjectedSS] = useState(initialData?.socialSecurity?.client1?.projectedAmount ?? 0);
  const [client2CurrentSS, setClient2CurrentSS] = useState(initialData?.socialSecurity?.client2?.currentAmount ?? 0);
  const [client2ProjectedSS, setClient2ProjectedSS] = useState(initialData?.socialSecurity?.client2?.projectedAmount ?? 0);

  // Savings: tax-deferred and tax-free are per-client, taxable is joint
  const [client1TaxDeferred, setClient1TaxDeferred] = useState(0);
  const [client1TaxFree, setClient1TaxFree] = useState(0);
  const [client2TaxDeferred, setClient2TaxDeferred] = useState(0);
  const [client2TaxFree, setClient2TaxFree] = useState(0);
  const [taxable, setTaxable] = useState(0);

  const [targetIncome, setTargetIncome] = useState(initialData?.goals?.targetIncome ?? 0);
  const [legacyGoal, setLegacyGoal] = useState(initialData?.goals?.legacyGoal ?? 0);

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // A pension exists when a benefit is entered — same rule Client Details uses
  // to show pension rows, so there's no separate yes/no to keep in sync.
  const client1HasPension = client1PensionAmount > 0;
  const client2HasPension = client2PensionAmount > 0;

  // Clear Client 2 fields when spouse is deselected
  useEffect(() => {
    if (!hasPartner) {
      setClient2FirstName('');
      setClient2LastName('');
      setClient2DOB('');
    }
  }, [hasPartner]);

  // Initialize savings from initial data
  useEffect(() => {
    if (initialData?.savings) {
      // Tax-deferred and tax-free are per-client
      const taxDeferredAccounts = initialData.savings.taxDeferred || [];
      const taxFreeAccounts = initialData.savings.taxFree || [];
      const taxableAccounts = initialData.savings.taxable || [];

      const c1TaxDeferred = taxDeferredAccounts
        .filter(acc => acc.owner === 'client1')
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      const c2TaxDeferred = taxDeferredAccounts
        .filter(acc => acc.owner === 'client2')
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);

      const c1TaxFree = taxFreeAccounts
        .filter(acc => acc.owner === 'client1')
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);
      const c2TaxFree = taxFreeAccounts
        .filter(acc => acc.owner === 'client2')
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);

      // Taxable is joint (sum all)
      const totalTaxable = taxableAccounts
        .reduce((sum, acc) => sum + (acc.balance || 0), 0);

      setClient1TaxDeferred(c1TaxDeferred);
      setClient2TaxDeferred(c2TaxDeferred);
      setClient1TaxFree(c1TaxFree);
      setClient2TaxFree(c2TaxFree);
      setTaxable(totalTaxable);
    }
  }, [initialData]);

  // Mark field as touched
  const handleBlur = (fieldName: string) => {
    setTouched(prev => new Set(prev).add(fieldName));
  };

  // Handle Client 1 Last Name blur with auto-fill logic
  const handleClient1LastNameBlur = () => {
    handleBlur('client1LastName');
    // Auto-fill Client 2 last name if spouse is selected and Client 2 last name is empty
    if (hasPartner && !client2LastName && client1LastName) {
      setClient2LastName(client1LastName);
    }
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Real-time validation
  useEffect(() => {
    const newErrors: FormErrors = {};

    // Personal Information — skipped in a revision: the section is hidden (identity +
    // email are managed on the Client Details tab), so those fields must never produce
    // errors here or the "fix the errors" alert flashes on an otherwise-valid submit.
    if (!readOnlyClientFields) {
      if (touched.has('client1FirstName') && !client1FirstName.trim()) {
        newErrors.client1FirstName = 'Client 1 first name is required';
      }
      if (touched.has('client1LastName') && !client1LastName.trim()) {
        newErrors.client1LastName = 'Client 1 last name is required';
      }
      if (touched.has('client1DOB') && !client1DOB) {
        newErrors.client1DOB = 'Client 1 date of birth is required';
      }
      if (touched.has('contactEmail') && !validateEmail(contactEmail)) {
        newErrors.contactEmail = 'Valid email is required';
      }

      // Partner fields (conditional)
      if (hasPartner) {
        if (touched.has('client2FirstName') && !client2FirstName.trim()) {
          newErrors.client2FirstName = 'Client 2 first name is required';
        }
        if (touched.has('client2LastName') && !client2LastName.trim()) {
          newErrors.client2LastName = 'Client 2 last name is required';
        }
        if (touched.has('client2DOB') && !client2DOB) {
          newErrors.client2DOB = 'Client 2 date of birth is required';
        }
      }
    }

    // Goals
    if (touched.has('targetIncome') && targetIncome <= 0) {
      newErrors.targetIncome = 'Target income must be greater than 0';
    }

    // Legacy Goal validation (optional but can't be negative)
    if (touched.has('legacyGoal') && legacyGoal < 0) {
      newErrors.legacyGoal = 'Legacy goal cannot be negative';
    }

    setErrors(newErrors);
  }, [
    client1FirstName, client1LastName, client1DOB, contactEmail,
    client2FirstName, client2LastName, client2DOB,
    targetIncome, legacyGoal, hasPartner, touched, readOnlyClientFields
  ]);

  // Check if form is valid
  const isFormValid = (): boolean => {
    // In a revision the Personal Information section is hidden — client identity
    // (name/DOB) and contact email are managed on the Client Details tab and come
    // from the existing plan, so only the planning assumptions shown here matter.
    if (readOnlyClientFields) {
      return targetIncome > 0;
    }
    return (
      client1FirstName.trim() !== '' &&
      client1LastName.trim() !== '' &&
      client1DOB !== '' &&
      validateEmail(contactEmail) &&
      targetIncome > 0 &&
      (hasPartner ? (
        client2FirstName.trim() !== '' &&
        client2LastName.trim() !== '' &&
        client2DOB !== ''
      ) : true)
    );
  };

  // Handle submit
  const handleSubmit = async () => {
    // Mark all VISIBLE fields as touched to show errors. In a revision the Personal
    // Information section is hidden, so touching those fields would only conjure
    // errors for inputs the advisor can't see or fix here.
    setTouched(new Set(
      readOnlyClientFields
        ? ['targetIncome']
        : [
            'client1FirstName', 'client1LastName', 'client1DOB', 'contactEmail',
            'client2FirstName', 'client2LastName', 'client2DOB', 'targetIncome'
          ]
    ));

    if (!isFormValid()) {
      return;
    }

    // Build WizardData object
    const formData: WizardData = {
      personal: {
        hasPartner,
        client1: {
          firstName: client1FirstName,
          lastName: client1LastName,
          dateOfBirth: client1DOB
        },
        client2: {
          firstName: client2FirstName,
          lastName: client2LastName,
          dateOfBirth: client2DOB
        },
        contact: {
          email: contactEmail,
          phone: '',
          advisorFirstName: '',
          advisorLastName: ''
        }
      },
      employment: {
        client1: {
          isRetired: false,
          currentIncome: client1Income,
          expectedRetirementAge: client1RetirementAge,
          hasPension: client1HasPension,
          pensionStartAge: client1HasPension ? client1PensionStartAge : undefined,
          pensionBenefitAmount: client1HasPension ? client1PensionAmount : undefined
        },
        client2: {
          isRetired: false,
          currentIncome: client2Income,
          expectedRetirementAge: client2RetirementAge,
          hasPension: client2HasPension,
          pensionStartAge: client2HasPension ? client2PensionStartAge : undefined,
          pensionBenefitAmount: client2HasPension ? client2PensionAmount : undefined
        }
      },
      socialSecurity: {
        client1: {
          currentAmount: client1CurrentSS,
          projectedAmount: client1ProjectedSS
        },
        client2: {
          currentAmount: client2CurrentSS,
          projectedAmount: client2ProjectedSS
        }
      },
      savings: {
        taxDeferred: [
          ...(client1TaxDeferred > 0 ? [{ accountName: 'Client 1 Tax-Deferred', balance: client1TaxDeferred, owner: 'client1' as const }] : []),
          ...(client2TaxDeferred > 0 ? [{ accountName: 'Client 2 Tax-Deferred', balance: client2TaxDeferred, owner: 'client2' as const }] : [])
        ],
        taxFree: [
          ...(client1TaxFree > 0 ? [{ accountName: 'Client 1 Tax-Free', balance: client1TaxFree, owner: 'client1' as const }] : []),
          ...(client2TaxFree > 0 ? [{ accountName: 'Client 2 Tax-Free', balance: client2TaxFree, owner: 'client2' as const }] : [])
        ],
        taxable: taxable > 0 ? [{ accountName: 'Taxable (Joint)', balance: taxable, owner: 'joint' as const }] : []
      },
      goals: {
        targetIncome,
        ...(legacyGoal > 0 ? { legacyGoal } : {}),
        selected: {
          financialIndependence: false,
          travel: false,
          legacy: false,
          healthcare: false,
          lifestyle: false,
          other: false
        },
        otherGoalDescription: ''
      },
      completed: false,
      uiState: {
        hasAnsweredPartnerQuestion: true,
        hasAnsweredRetirementQuestion: true,
        hasAnsweredPartnerRetirementQuestion: hasPartner,
        hasAnsweredSocialSecurityQuestion: true,
        hasAnsweredPartnerSocialSecurityQuestion: hasPartner,
        hasAnsweredGoalsQuestion: true
      }
    };

    await onSubmit(formData, '');
  };

  const valid = isFormValid();
  const c1Name = client1FirstName.trim() || 'Client 1';
  const c2Name = client2FirstName.trim() || 'Client 2';
  // Whole-dollar currency rows: null (cleared) reads as 0.
  const money = (set: (n: number) => void) => (v: number | null) => set(v ?? 0);
  const age = (set: (n: number) => void) => (v: number | null) => set(v ?? 0);

  // The name IS the column header, typed straight into it — same as editing an
  // existing client on the Client Details tab.
  const nameHeader = (who: 'client1' | 'client2') => {
    const first = who === 'client1' ? client1FirstName : client2FirstName;
    const last = who === 'client1' ? client1LastName : client2LastName;
    const setFirst = who === 'client1' ? setClient1FirstName : setClient2FirstName;
    const setLast = who === 'client1' ? setClient1LastName : setClient2LastName;
    const firstErr = errors[`${who}FirstName`];
    const lastErr = errors[`${who}LastName`];
    return (
      <>
        <ColHeader>
          <Stack direction="row" sx={{ gap: '6px' }}>
            <InputBase
              value={first}
              placeholder={who === 'client1' ? 'First name' : 'Partner first name'}
              onChange={(e) => setFirst(e.target.value)}
              onBlur={() => handleBlur(`${who}FirstName`)}
              disabled={submitting}
              sx={{ ...nameEditorSx, ...(firstErr ? { borderColor: '#DC2626' } : {}) }}
            />
            <InputBase
              value={last}
              placeholder={who === 'client1' ? 'Last name' : 'Partner last name'}
              onChange={(e) => setLast(e.target.value)}
              onBlur={who === 'client1' ? handleClient1LastNameBlur : () => handleBlur('client2LastName')}
              disabled={submitting}
              sx={{ ...nameEditorSx, ...(lastErr ? { borderColor: '#DC2626' } : {}) }}
            />
          </Stack>
        </ColHeader>
        {(firstErr || lastErr) && (
          <Typography sx={{ fontSize: 10.5, color: '#DC2626', mt: '-2px', mb: '4px' }}>
            {firstErr || lastErr}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box>
      {/* Action bar — same chrome as the Client Details "Update & rerun" bar.
          Sticky so the submit stays reachable on a long form. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          px: '18px',
          py: '13px',
          mb: 1.75,
          bgcolor: tokens.card,
          border: `1px solid ${tokens.border}`,
          borderLeft: `4px solid ${tokens.navy}`,
          borderRadius: '0 8px 8px 0',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.text, mb: '2px' }}>
            {readOnlyClientFields ? 'Revise this plan' : 'Create a plan request'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.textSec }}>
            {readOnlyClientFields
              ? 'Adjust the planning assumptions below, then submit to rerun.'
              : "Enter the household's details — PlanScout estimates anything you skip."}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexShrink={0}>
          <Button size="small" onClick={onCancel} disabled={submitting} sx={bannerQuietButtonSx}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={!valid || submitting}
            startIcon={
              submitting ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 15 }} />
            }
            sx={bannerRunButtonSx(valid)}
          >
            {submitting ? 'Submitting…' : 'Submit plan request'}
          </Button>
        </Stack>
      </Box>

      {/* Error Summary */}
      {Object.keys(errors).length > 0 && touched.size > 0 && (
        <Alert severity="error" sx={{ mb: 1.75 }}>
          Please fix the errors below before submitting.
        </Alert>
      )}

      <Stack sx={{ gap: '6px' }}>
        {/* ── Bio — hidden in a revision (readOnlyClientFields): client identity +
            contact email are managed on the Client Details tab, which is the
            accurate source and cascades to the plan. */}
        {!readOnlyClientFields && (
          <CompactSection
            icon={<UserGlyph size={12} />}
            title="Bio"
            subtitle="Names · DOB · Retirement age"
            open
            collapsible={false}
          >
            <Box sx={personGridSx(true)}>
              <Box>
                {nameHeader('client1')}
                <CompactRow
                  label="Date of birth"
                  control="date"
                  value={client1DOB}
                  onChange={(v) => setClient1DOB(v ?? '')}
                  onBlur={() => handleBlur('client1DOB')}
                  error={errors.client1DOB}
                  required
                  editing
                />
                <CompactRow
                  label="Retirement age"
                  control="number"
                  value={client1RetirementAge}
                  suffix="yrs"
                  placeholder="—"
                  onChange={age(setClient1RetirementAge)}
                  editing
                />
              </Box>
              <Box>
                {hasPartner ? (
                  <>
                    {nameHeader('client2')}
                    <CompactRow
                      label="Date of birth"
                      control="date"
                      value={client2DOB}
                      onChange={(v) => setClient2DOB(v ?? '')}
                      onBlur={() => handleBlur('client2DOB')}
                      error={errors.client2DOB}
                      required
                      editing
                    />
                    <CompactRow
                      label="Retirement age"
                      control="number"
                      value={client2RetirementAge}
                      suffix="yrs"
                      placeholder="—"
                      onChange={age(setClient2RetirementAge)}
                      editing
                    />
                    <Button
                      size="small"
                      onClick={() => setHasPartner(false)}
                      disabled={submitting}
                      sx={{ mt: '6px', p: 0, minWidth: 0, fontSize: 11.5, color: tokens.textMut, textTransform: 'none' }}
                    >
                      Remove partner
                    </Button>
                  </>
                ) : (
                  <>
                    <ColHeader>Partner / spouse</ColHeader>
                    <Button
                      size="small"
                      onClick={() => setHasPartner(true)}
                      disabled={submitting}
                      sx={{ mt: '6px', p: 0, minWidth: 0, fontSize: 12, fontWeight: 600, color: tokens.navy, textTransform: 'none' }}
                    >
                      + Add a partner or spouse
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            <Box sx={householdBlockSx}>
              <ColHeader>Household</ColHeader>
              <CompactRow
                label="Contact email"
                control="text"
                value={contactEmail}
                onChange={(v) => setContactEmail(v ?? '')}
                onBlur={() => handleBlur('contactEmail')}
                error={errors.contactEmail}
                placeholder="name@example.com"
                required
                editing
              />
            </Box>
          </CompactSection>
        )}

        {/* ── Income ── */}
        <CompactSection
          icon={<ClockGlyph size={12} />}
          title="Income"
          subtitle="Wages · Social Security · Pensions · Target cash flow"
          open
          collapsible={false}
        >
          <Box sx={personGridSx(hasPartner)}>
            {(hasPartner ? (['client1', 'client2'] as const) : (['client1'] as const)).map((who) => {
              const isC1 = who === 'client1';
              return (
                <Box key={who}>
                  <ColHeader>{isC1 ? c1Name : c2Name}</ColHeader>
                  <CompactRow
                    label="Current wages"
                    control="currency"
                    value={isC1 ? client1Income : client2Income}
                    onChange={money(isC1 ? setClient1Income : setClient2Income)}
                    placeholder="0"
                    editing
                  />
                  <CompactRow
                    label="Social Security at FRA"
                    control="currency"
                    value={isC1 ? client1ProjectedSS : client2ProjectedSS}
                    onChange={money(isC1 ? setClient1ProjectedSS : setClient2ProjectedSS)}
                    placeholder="0"
                    editing
                  />
                  <CompactRow
                    label="Social Security now (if claiming)"
                    control="currency"
                    value={isC1 ? client1CurrentSS : client2CurrentSS}
                    onChange={money(isC1 ? setClient1CurrentSS : setClient2CurrentSS)}
                    placeholder="0"
                    editing
                  />
                  <CompactRow
                    label="Pension benefit"
                    control="currency"
                    value={isC1 ? client1PensionAmount : client2PensionAmount}
                    onChange={money(isC1 ? setClient1PensionAmount : setClient2PensionAmount)}
                    placeholder="0"
                    editing
                  />
                  {(isC1 ? client1HasPension : client2HasPension) && (
                    <CompactRow
                      label="Pension start age"
                      control="number"
                      value={isC1 ? client1PensionStartAge : client2PensionStartAge}
                      placeholder="—"
                      onChange={age(isC1 ? setClient1PensionStartAge : setClient2PensionStartAge)}
                      editing
                    />
                  )}
                </Box>
              );
            })}
          </Box>
          <Box sx={householdBlockSx}>
            <ColHeader>Household</ColHeader>
            <CompactRow
              label="Annual Cashflow (After-Tax)"
              control="currency"
              value={targetIncome}
              onChange={money(setTargetIncome)}
              onBlur={() => handleBlur('targetIncome')}
              error={errors.targetIncome}
              placeholder="0"
              required
              editing
            />
          </Box>
        </CompactSection>

        {/* ── Accounts ── */}
        <CompactSection
          icon={<ClipboardGlyph size={12} />}
          title="Accounts"
          subtitle="Investment accounts and balances"
          open
          collapsible={false}
        >
          <Box sx={personGridSx(hasPartner)}>
            {(hasPartner ? (['client1', 'client2'] as const) : (['client1'] as const)).map((who) => {
              const isC1 = who === 'client1';
              return (
                <Box key={who}>
                  <ColHeader>{isC1 ? c1Name : c2Name}</ColHeader>
                  <CompactRow
                    label="Tax-deferred (IRA/401k)"
                    control="currency"
                    value={isC1 ? client1TaxDeferred : client2TaxDeferred}
                    onChange={money(isC1 ? setClient1TaxDeferred : setClient2TaxDeferred)}
                    placeholder="0"
                    editing
                  />
                  <CompactRow
                    label="Tax-free (Roth)"
                    control="currency"
                    value={isC1 ? client1TaxFree : client2TaxFree}
                    onChange={money(isC1 ? setClient1TaxFree : setClient2TaxFree)}
                    placeholder="0"
                    editing
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={householdBlockSx}>
            <ColHeader>Household</ColHeader>
            <CompactRow
              label="Taxable accounts (Joint)"
              control="currency"
              value={taxable}
              onChange={money(setTaxable)}
              placeholder="0"
              editing
            />
            <CompactRow
              label="Legacy goal (optional)"
              control="currency"
              value={legacyGoal}
              onChange={money(setLegacyGoal)}
              onBlur={() => handleBlur('legacyGoal')}
              error={errors.legacyGoal}
              placeholder="0"
              editing
            />
          </Box>
        </CompactSection>
      </Stack>

      <Typography sx={{ mt: '14px', fontSize: 10.5, color: tokens.textMut, lineHeight: 1.6 }}>
        Anything left blank is estimated by PlanScout from the household's income and assets.
        {readOnlyClientFields && initialData?.goals?.legacyGoal
          ? ` Original legacy goal: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(initialData.goals.legacyGoal)}.`
          : ''}
      </Typography>
    </Box>
  );
};

export default PlanRequestForm;
